import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import API_BASE_URL from "../../utils/api";
import { useNavigate } from "react-router-dom";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const emptyForm = {
  name: "",
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  isActive: true,
};

const formatLocalDateInput = (value) => {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatLocalTimeInput = (value) => {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

const TranscribeathonAdminPanel = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantEmail, setParticipantEmail] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingEventId, setEditingEventId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  const resetNotices = () => {
    setMessage("");
    setError("");
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/transcribeathon/admin/events`,
        { headers: authHeaders() }
      );

      setEvents(res.data);

      if (!selectedEvent && res.data.length > 0) {
        setSelectedEvent(res.data[0]);
      } else if (selectedEvent) {
        const refreshedSelected = res.data.find(
          (event) => event._id === selectedEvent._id
        );
        if (refreshedSelected) {
          setSelectedEvent(refreshedSelected);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch events");
    }
  };

  const fetchParticipants = async (eventId) => {
    try {
      if (!eventId) {
        setParticipants([]);
        return;
      }

      const res = await axios.get(
        `${API_BASE_URL}/transcribeathon/admin/events/${eventId}/participants`,
        { headers: authHeaders() }
      );

      setParticipants(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch participants");
    }
  };

  useEffect(() => {
    document.title = "Manage Transcribe-athon event - MS Digital Hub";
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent?._id) {
      fetchParticipants(selectedEvent._id);
    } else {
      setParticipants([]);
    }
  }, [selectedEvent]);
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: authHeaders(),
          withCredentials: true,
        });
        setCurrentUser(res.data);
      } catch (err) {
        console.error("Failed to fetch current user", err);
      }
    };

    fetchCurrentUser();
  }, []);

  const buildDateTime = (date, time) => `${date}T${time}:00`;

  const handleCreateOrUpdateEvent = async () => {
    resetNotices();

    try {
      if (
        !form.name.trim() ||
        !form.startDate ||
        !form.startTime ||
        !form.endDate ||
        !form.endTime
      ) {
        setError("Please fill in all event fields");
        return;
      }

      const startsAt = buildDateTime(form.startDate, form.startTime);
      const endsAt = buildDateTime(form.endDate, form.endTime);

      if (new Date(startsAt) >= new Date(endsAt)) {
        setError("End date/time must be after start date/time");
        return;
      }

      if (editingEventId) {
        const res = await axios.patch(
          `${API_BASE_URL}/transcribeathon/admin/events/${editingEventId}`,
          {
            name: form.name.trim(),
            startsAt,
            endsAt,
            isActive: form.isActive,
          },
          { headers: authHeaders() }
        );

        setMessage(res.data.message || "Event updated");
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/transcribeathon/admin/events`,
          {
            name: form.name.trim(),
            startsAt,
            endsAt,
            isActive: form.isActive,
          },
          { headers: authHeaders() }
        );

        setMessage(res.data.message || "Event created");
      }

      setForm(emptyForm);
      setEditingEventId(null);
      await fetchEvents();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save event");
    }
  };

  const handleEditEvent = (event) => {
    setEditingEventId(event._id);
    setForm({
      name: event.name || "",
      startDate: formatLocalDateInput(event.startsAt),
      startTime: formatLocalTimeInput(event.startsAt),
      endDate: formatLocalDateInput(event.endsAt),
      endTime: formatLocalTimeInput(event.endsAt),
      isActive: Boolean(event.isActive),
    });
    setTab(0);
  };

  const handleDeleteEvent = async (eventId) => {
    resetNotices();

    try {
      await axios.delete(
        `${API_BASE_URL}/transcribeathon/admin/events/${eventId}`,
        { headers: authHeaders() }
      );

      setMessage("Event deleted successfully");

      if (selectedEvent?._id === eventId) {
        setSelectedEvent(null);
        setParticipants([]);
      }

      await fetchEvents();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete event");
    }
  };

  const handleAddParticipant = async () => {
    resetNotices();

    try {
      if (!selectedEvent?._id) {
        setError("Please select an event first");
        return;
      }

      if (!participantEmail.trim()) {
        setError("Please enter participant email");
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/transcribeathon/admin/events/${selectedEvent._id}/participants`,
        { email: participantEmail.trim() },
        { headers: authHeaders() }
      );

      setMessage(res.data.message || "Participant added");
      setParticipantEmail("");
      await fetchParticipants(selectedEvent._id);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add participant");
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    resetNotices();

    try {
      await axios.delete(
        `${API_BASE_URL}/transcribeathon/admin/events/${selectedEvent._id}/participants/${participantId}`,
        { headers: authHeaders() }
      );

      setMessage("Participant removed");
      await fetchParticipants(selectedEvent._id);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove participant");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {" "}
      <Box
        sx={{
          width: "auto",
          margin: "0px auto",
          padding: { xs: "20px", sm: "30px", md: "1px 80px" },
          border: "none",
          backgroundColor: "#ffffff",
          borderRadius: "8px",
        }}
      >
        {" "}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Transcribe-athon Admin{" "}
        </Typography>{" "}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}{" "}
          </Alert>
        )}{" "}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}{" "}
          </Alert>
        )}{" "}
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
          <Tab label="Events" />
          <Tab label="Participants" />{" "}
        </Tabs>{" "}
        {tab === 0 && (
          <Box>
            {" "}
            {currentUser?.isSuperAdmin === true && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {editingEventId ? "Edit Event" : "Create Event"}
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label="Event Name"
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    fullWidth
                  />

                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={form.startDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Start Time"
                      type="time"
                      value={form.startTime}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          startTime: e.target.value,
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={form.endDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      value={form.endTime}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          endTime: e.target.value,
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isActive}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            isActive: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Mark this event as active"
                  />

                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      onClick={handleCreateOrUpdateEvent}
                    >
                      {editingEventId ? "Save Changes" : "Create Event"}
                    </Button>

                    {editingEventId && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setEditingEventId(null);
                          setForm(emptyForm);
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Box>
                </Stack>

                <Divider sx={{ my: 3 }} />
              </Box>
            )}
            <Typography variant="h6" sx={{ mb: 2 }}>
              All Events{" "}
            </Typography>{" "}
            {events.length === 0 ? (
              <Typography color="text.secondary">
                No events created yet.{" "}
              </Typography>
            ) : (
              <List>
                {" "}
                {events.map((event) => (
                  <ListItem
                    key={event._id}
                    divider
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {" "}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedEvent(event)}
                        >
                          Select{" "}
                        </Button>{" "}
                        {currentUser?.isSuperAdmin === true && (
                          <>
                            <IconButton onClick={() => handleEditEvent(event)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteEvent(event._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            navigate(`/transcribeathon-events/${event._id}`)
                          }
                        >
                          Open{" "}
                        </Button>{" "}
                      </Box>
                    }
                  >
                    {" "}
                    <ListItemText
                      primary={event.name}
                      secondary={`${new Date(
                        event.startsAt
                      ).toLocaleString()} — ${new Date(
                        event.endsAt
                      ).toLocaleString()}${event.isActive ? " • Active" : ""}`}
                    />{" "}
                  </ListItem>
                ))}{" "}
              </List>
            )}{" "}
          </Box>
        )}{" "}
        {tab === 1 && (
          <Box>
            {" "}
            <Typography variant="h6" sx={{ mb: 1 }}>
              Add Participants{" "}
            </Typography>{" "}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selected event: {selectedEvent ? selectedEvent.name : "None"}{" "}
            </Typography>{" "}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
              {" "}
              <TextField
                label="Participant Email"
                value={participantEmail}
                onChange={(e) => setParticipantEmail(e.target.value)}
                fullWidth
              />{" "}
              <Button variant="contained" onClick={handleAddParticipant}>
                Add Participant{" "}
              </Button>{" "}
            </Box>
            <Divider sx={{ mb: 2 }} />{" "}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Participants{" "}
            </Typography>{" "}
            {participants.length === 0 ? (
              <Typography color="text.secondary">
                No participants added for this event.{" "}
              </Typography>
            ) : (
              <List>
                {" "}
                {participants.map((participant) => (
                  <ListItem
                    key={participant._id}
                    divider
                    secondaryAction={
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveParticipant(participant._id)}
                      >
                        Remove{" "}
                      </Button>
                    }
                  >
                    {" "}
                    <ListItemText
                      primary={
                        participant?.userId?.name ||
                        participant.displayNameSnapshot
                      }
                      secondary={participant?.userId?.email || ""}
                    />{" "}
                  </ListItem>
                ))}{" "}
              </List>
            )}{" "}
          </Box>
        )}{" "}
      </Box>{" "}
    </Box>
  );
};

export default TranscribeathonAdminPanel;
